import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Microphone, PaperPlaneRight, X, ImageSquare, Waveform, Ear, Keyboard } from "@phosphor-icons/react";
import JaredPermissionAlert from "@/components/JaredPermissionAlert";
import {
  queryPermission, requestMicrophone, requestNotifications, notificationsSupported,
  acquireWakeLock, releaseWakeLock, runSessionDiagnostics, logPermissionEvent,
  speechRecognitionAvailable,
} from "@/lib/devicePermissions";
import i18n from "@/i18n";
import { pickVoice } from "@/lib/jaredVoice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Idioma activo de la app (ES/EN) para respuestas y voz de Jared
const langCode = () => ((i18n.language || "es").toLowerCase().startsWith("en") ? "en" : "es");
const speechLang = () => (langCode() === "en" ? "en-US" : "es-MX");
const L = (es, en) => (langCode() === "en" ? en : es);

// Colores del núcleo según estado de Jared
const STATE_COLOR = {
  normal: { c: "#10E0C4", glow: "#0BA895", label: "En línea" },
  warning: { c: "#F5C518", glow: "#B8901A", label: "Procesando / aviso" },
  alert: { c: "#FF3B3B", glow: "#B71C1C", label: "Riesgo detectado" },
  done: { c: "#FFFFFF", glow: "#9FE8FF", label: "Acción completada" },
};

/* ---------- Núcleo holográfico (Canvas) ---------- */
function Orb({ state = "normal", active = false, speaking = false, size = 64 }) {
  const ref = useRef(null);
  const raf = useRef(null);
  const partsRef = useRef([]);
  const tRef = useRef(0);
  const ampRef = useRef(0);
  const activeRef = useRef(active);
  const speakingRef = useRef(speaking);
  const stateRef = useRef(state);
  activeRef.current = active;
  speakingRef.current = speaking;
  stateRef.current = state;

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    const cx = size / 2, cy = size / 2;

    if (partsRef.current.length === 0) {
      partsRef.current = Array.from({ length: 22 }, () => ({
        ang: Math.random() * Math.PI * 2,
        radF: 0.24 + Math.random() * 0.2,
        spd: (0.006 + Math.random() * 0.02) * (Math.random() > 0.5 ? 1 : -1),
        szF: (0.6 + Math.random() * 1.6) / 150,
      }));
    }

    const draw = () => {
      tRef.current += 1;
      const t = tRef.current;
      const col = STATE_COLOR[stateRef.current] || STATE_COLOR.normal;
      const spk = speakingRef.current;
      const act = activeRef.current || spk;
      // Amplitud tipo voz cuando Jared habla (varias ondas + ruido), suavizada
      let target;
      if (spk) {
        const v = Math.abs(Math.sin(t * 0.5) * 0.6 + Math.sin(t * 0.23) * 0.3 + Math.sin(t * 0.11) * 0.2)
          + Math.random() * 0.18;
        target = Math.min(1, v);
      } else {
        target = act ? 0.5 : 0.2;
      }
      ampRef.current += (target - ampRef.current) * 0.35;
      const amp = ampRef.current;
      const pulse = spk ? amp : (0.5 + 0.5 * Math.sin(t * (act ? 0.14 : 0.06)));
      const intensity = spk ? (0.8 + amp * 0.5) : (act ? 1 : 0.72);
      ctx.clearRect(0, 0, size, size);

      // halo exterior
      const halo = ctx.createRadialGradient(cx, cy, size * 0.05, cx, cy, size * 0.5);
      halo.addColorStop(0, hexA(col.c, 0.35 * intensity));
      halo.addColorStop(0.6, hexA(col.glow, 0.12 * intensity));
      halo.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, size, size);

      // anillos de energía
      for (let r = 0; r < 2; r++) {
        ctx.beginPath();
        const rr = size * (0.30 + r * 0.08) + pulse * (spk ? 6 : 2);
        ctx.strokeStyle = hexA(col.c, (0.5 - r * 0.18) * intensity);
        ctx.lineWidth = 1.2 + (spk ? amp * 1.4 : 0);
        const off = t * (0.01 + r * 0.006) * (r % 2 ? -1 : 1);
        ctx.arc(cx, cy, rr, off, off + Math.PI * 1.5);
        ctx.stroke();
      }

      // partículas orbitando
      partsRef.current.forEach((p) => {
        p.ang += p.spd * (act ? 1.8 : 1) * (spk ? 1 + amp * 0.8 : 1);
        const rad = p.radF * size;
        const px = cx + Math.cos(p.ang) * rad;
        const py = cy + Math.sin(p.ang) * rad * 0.62;
        ctx.beginPath();
        ctx.fillStyle = hexA(col.c, 0.85 * intensity);
        ctx.arc(px, py, Math.max(1, p.szF * size), 0, Math.PI * 2);
        ctx.fill();
      });

      // núcleo central (late con la voz)
      const coreR = size * 0.16 * (1 + pulse * (spk ? 0.5 : 0.18) * (act ? 1.4 : 1));
      const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      core.addColorStop(0, "#ffffff");
      core.addColorStop(0.4, col.c);
      core.addColorStop(1, hexA(col.glow, 0.15));
      ctx.beginPath();
      ctx.fillStyle = core;
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();

      raf.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf.current);
  }, [size]);

  return <canvas ref={ref} style={{ width: size, height: size, display: "block" }} />;
}

function hexA(hex, a) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map(x => x + x).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/* ---------- Asistente Jared ---------- */
export default function JaredAssistant() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [orbCentered, setOrbCentered] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [jState, setJState] = useState("normal");
  const [voiceOn, setVoiceOn] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [telemetry, setTelemetry] = useState(null);
  const [insight, setInsight] = useState({ mod: null, loading: false, data: null });
  const [autopilot, setAutopilot] = useState({ enabled: true, acciones: {}, suggestions: [], canExecute: false, busy: null, log: [], showLog: false });
  const [profile, setProfile] = useState(null);
  const [permAlert, setPermAlert] = useState(null);
  const [wake, setWake] = useState(() => {
    try { return localStorage.getItem("jared_wake") === "1"; } catch { return false; }
  });
  const recRef = useRef(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const sessionId = useRef(`jared_${Date.now()}`);
  const wakeRef = useRef(null);
  const wakeEnabledRef = useRef(false);
  const commandActiveRef = useRef(false);
  const thinkingRef = useRef(false);
  const listeningRef = useRef(false);
  const speakingRef = useRef(false);
  const sendRef = useRef(() => {});
  const activateRef = useRef(() => {});
  const cfgRef = useRef({ wake_word: "jared", voz_nombre: "", voz_rate: 1.03, voz_genero: "auto" });
  const speechUnlockedRef = useRef(false);
  const voicesRef = useRef([]);
  const commandSentRef = useRef(false);
  const greetedRef = useRef(false);
  const unmountedRef = useRef(false);

  useEffect(() => {
    if (msgs.length === 0) {
      const fallback = L(
        "Hola, soy Jared, tu asistente de seguridad de AvilesSafety360. Pregúntame por el estado de la planta, riesgos, permisos por vencer o cualquier módulo. Puedes escribir, hablarme o enviarme una foto de un hallazgo.",
        "Hi, I'm Jared, your AvilesSafety360 safety assistant. Ask me about the plant status, risks, expiring permits or any module. You can type, talk to me, or send a photo of a finding."
      );
      setMsgs([{ role: "jared", text: fallback, state: "normal" }]);
      axios.post(`${API}/jared/greeting?lang=${langCode()}`, {}, { withCredentials: true })
        .then(({ data }) => {
          if (data?.reply) setMsgs([{ role: "jared", text: data.reply, state: data.state || "normal" }]);
          if (data?.telemetry) setTelemetry(data.telemetry);
          loadAutopilot();
          loadProfile();
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, thinking]);

  // IA Explicativa: abre/cierra el panel "¿Por qué?" de una tarjeta y trae los
  // registros/variables que originan la alerta + narrativa IA (aislado por tenant).
  const openInsight = async (mod) => {
    if (insight.mod === mod) { setInsight({ mod: null, loading: false, data: null }); return; }
    setInsight({ mod, loading: true, data: null });
    try {
      const { data } = await axios.get(
        `${API}/jared/insights?modulo=${mod}&lang=${langCode()}`, { withCredentials: true });
      setInsight({ mod, loading: false, data });
    } catch {
      setInsight({ mod, loading: false, data: { error: true } });
    }
  };

  // IA Autónoma: Autopiloto — carga propuestas + config (aislado por tenant)
  const loadAutopilot = async () => {
    try {
      const [cfg, sug] = await Promise.all([
        axios.get(`${API}/jared/autopilot/config`, { withCredentials: true }),
        axios.get(`${API}/jared/autopilot/suggestions`, { withCredentials: true }),
      ]);
      setAutopilot(a => ({ ...a, enabled: cfg.data.enabled, acciones: cfg.data.acciones || {},
        suggestions: sug.data.suggestions || [], canExecute: sug.data.can_execute }));
    } catch { /* silencioso */ }
  };

  // Confirmación 1 clic de una propuesta del autopiloto
  const execAutopilot = async (s) => {
    setAutopilot(a => ({ ...a, busy: s.id }));
    try {
      const { data } = await axios.post(`${API}/jared/autopilot/execute`,
        { tipo: s.tipo, payload: s.payload }, { withCredentials: true });
      setAutopilot(a => ({ ...a, busy: null, suggestions: a.suggestions.filter(x => x.id !== s.id) }));
      setMsgs(m => [...m, { role: "jared", text: data.mensaje, state: "done" }]);
      unlockSpeech(); speak(data.mensaje);
    } catch {
      setAutopilot(a => ({ ...a, busy: null }));
      setMsgs(m => [...m, { role: "jared", text: L("No pude ejecutar la acción. Intenta de nuevo.", "I couldn't run the action. Try again."), state: "warning" }]);
    }
  };

  const toggleAutopilot = async (enabled) => {
    setAutopilot(a => ({ ...a, enabled }));
    try {
      await axios.put(`${API}/jared/autopilot/config`,
        { enabled, acciones: autopilot.acciones }, { withCredentials: true });
    } catch { /* noop */ }
    if (enabled) loadAutopilot();
    else setAutopilot(a => ({ ...a, suggestions: [] }));
  };

  const loadAutopilotLog = async () => {
    try {
      const { data } = await axios.get(`${API}/jared/autopilot/log?limit=15`, { withCredentials: true });
      setAutopilot(a => ({ ...a, log: data.log || [], showLog: true }));
    } catch { /* noop */ }
  };

  // IA Adaptativa: carga el perfil de la planta (promedios + áreas recurrentes)
  const loadProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/jared/plant-profile`, { withCredentials: true });
      setProfile(data);
    } catch { /* noop */ }
  };

  // Auto-despliega el panel HUD solo cuando hay algo visual que mostrar (PDF o hallazgo por registrar)
  useEffect(() => {
    if (!open || expanded) return;
    const last = [...msgs].reverse().find(m => m.role === "jared");
    if (last && (last.downloadUrl || (last.finding && !last.registered))) setExpanded(true);
  }, [msgs, open]); // eslint-disable-line

  // Al abrir Jared con un gesto, lee en voz alta la PRIORIDAD DEL DÍA (o el saludo si no hay datos).
  // Espera brevemente a que llegue la telemetría; solo una vez por sesión.
  useEffect(() => {
    if (!open || greetedRef.current) return;
    const t = setTimeout(() => {
      if (greetedRef.current) return;
      greetedRef.current = true;
      const prio = computePriority(telemetry);
      const jgreet = [...msgs].reverse().find(m => m.role === "jared")?.text;
      let line;
      if (prio) {
        const top = computePriorities(telemetry).slice(0, 3).map(p => p.text);
        const head = langCode() === "en" ? "Top priorities: " : "Prioridades del día: ";
        line = head + top.join("; ") + ".";
      } else if (telemetry) {
        line = langCode() === "en" ? "All systems nominal." : "Todos los sistemas en estado nominal.";
      } else {
        line = jgreet;
      }
      speak(line);
    }, telemetry ? 350 : 1600);
    return () => clearTimeout(t);
  }, [open, telemetry, msgs]); // eslint-disable-line

  useEffect(() => { thinkingRef.current = thinking; }, [thinking]);
  useEffect(() => { listeningRef.current = listening; }, [listening]);

  // Carga de voces (en Android/Chrome se resuelven de forma asíncrona vía 'voiceschanged').
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices() || []; };
    load();
    window.speechSynthesis.addEventListener?.("voiceschanged", load);
    return () => { try { window.speechSynthesis.removeEventListener?.("voiceschanged", load); } catch { /* noop */ } };
  }, []);

  // Desbloquea la síntesis de voz DENTRO de un gesto del usuario (requisito de Chrome/Android).
  // Sin esto, un speak() disparado desde el reconocimiento de voz queda silenciado.
  function unlockSpeech() {
    if (speechUnlockedRef.current || !window.speechSynthesis) return;
    try {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0; u.lang = speechLang();
      window.speechSynthesis.resume();
      window.speechSynthesis.speak(u);
      speechUnlockedRef.current = true;
    } catch { /* noop */ }
  }

  // Reanuda el modo manos libres SOLO cuando Jared terminó de hablar (evita que el
  // micrófono abierto corte el TTS en Android).
  function resumeWakeSoon() {
    if (unmountedRef.current) return;
    if (wakeEnabledRef.current && !commandActiveRef.current) {
      setTimeout(() => { try { startWake(); } catch { /* noop */ } }, 400);
    }
  }

  useEffect(() => {
    axios.get(`${API}/jared/settings`, { withCredentials: true })
      .then(({ data }) => {
        cfgRef.current = {
          wake_word: (data.wake_word || "Jared").toLowerCase().trim() || "jared",
          voz_nombre: data.voz_nombre || "",
          voz_rate: data.voz_rate || 1.03,
          voz_genero: data.voz_genero || "auto",
        };
      }).catch(() => { /* usa defaults */ });
  }, []);

  useEffect(() => {
    if (wake) { wakeEnabledRef.current = true; startWake(); }
    return () => {
      // Al desmontar (cierre de sesión / salir de la app) se apaga TODO el modo de
      // voz para que Jared no siga escuchando ni respondiendo fuera de la sesión.
      unmountedRef.current = true;
      wakeEnabledRef.current = false;
      commandActiveRef.current = false;
      try { wakeRef.current && (wakeRef.current.onend = null, wakeRef.current.abort?.(), wakeRef.current.stop()); } catch { /* noop */ }
      wakeRef.current = null;
      try { recRef.current && (recRef.current.onend = null, recRef.current.abort?.(), recRef.current.stop()); } catch { /* noop */ }
      recRef.current = null;
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
      try { releaseWakeLock(); } catch { /* noop */ }
    };
  }, []); // eslint-disable-line

  // Mantén la pantalla encendida (Screen Wake Lock) mientras Jared está activo
  // hablando o escuchando; se libera al terminar o cerrar el panel.
  useEffect(() => {
    const active = open && (speaking || listening || thinking);
    if (active) acquireWakeLock(); else releaseWakeLock();
  }, [open, speaking, listening, thinking]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" &&
          (speakingRef.current || listeningRef.current)) acquireWakeLock();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Validación automática al inicio de cada sesión: mic, cámara, internet, altavoz y voz.
  useEffect(() => {
    (async () => {
      try {
        const d = await runSessionDiagnostics();
        logPermissionEvent("session_check", "run", { results: JSON.stringify(d) });
      } catch { /* noop */ }
    })();
  }, []);

  // Limpia el texto SOLO para la lectura por voz (no cambia lo que se muestra en pantalla).
  // Quita emojis/pictogramas, marcadores de plural "(s)/(es)/(as)", markdown y símbolos,
  // para que la síntesis de voz suene fluida y natural.
  function cleanForSpeech(text) {
    return (text || "")
      // Emojis y pictogramas (símbolos, banderas, dingbats, variation selectors)
      .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/gu, " ")
      .replace(/[*#_`>|]/g, " ")          // markdown / símbolos
      .replace(/\((?:e?s|as)\)/gi, "")     // "acto(s)" -> "acto", "condición(es)" -> "condición"
      .replace(/(\p{L})\/(\p{L})/gu, "$1 o $2") // "acto/condición" -> "acto o condición"
      .replace(/[·•▪◦]/g, ", ")           // viñetas / punto medio -> pausa
      .replace(/\s*\n\s*/g, ". ")          // saltos de línea -> pausa
      .replace(/\s{2,}/g, " ")             // espacios múltiples
      .replace(/\s+([.,;:])/g, "$1")       // espacio antes de puntuación
      .replace(/([.,;:])\1+/g, "$1")       // puntuación duplicada
      .trim();
  }

  // Cierra Jared con animación de "regreso a su lugar" (a la esquina/FAB)
  function closeJared() {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    try { recRef.current && recRef.current.stop(); } catch { /* noop */ }
    setClosing(true);
    setTimeout(() => {
      setOpen(false); setExpanded(false); setClosing(false);
      greetedRef.current = false;
    }, 340);
  }

  function speak(text, onEnd) {
    if (!voiceOn || !window.speechSynthesis || !text) {
      if (onEnd) onEnd(); else resumeWakeSoon();
      return;
    }
    try {
      // Cierra el micrófono mientras Jared habla: en Android el TTS se corta si el mic está abierto.
      try { wakeRef.current && wakeRef.current.stop(); wakeRef.current = null; } catch { /* noop */ }
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(cleanForSpeech(text));
      u.lang = speechLang();
      const voices = voicesRef.current.length ? voicesRef.current : (window.speechSynthesis.getVoices() || []);
      const match = pickVoice(voices, {
        vozNombre: cfgRef.current.voz_nombre,
        vozGenero: cfgRef.current.voz_genero,
        en: langCode() === "en",
      });
      if (match) u.voice = match;
      u.rate = cfgRef.current.voz_rate || 1.03; u.pitch = 1.0;
      speakingRef.current = true;
      setSpeaking(true);
      const finish = () => {
        speakingRef.current = false;
        setSpeaking(false);
        if (onEnd) onEnd(); else resumeWakeSoon();
      };
      u.onstart = () => { speakingRef.current = true; setSpeaking(true); };
      u.onend = finish;
      u.onerror = finish;
      try { window.speechSynthesis.resume(); } catch { /* noop */ }
      window.speechSynthesis.speak(u);
    } catch { speakingRef.current = false; setSpeaking(false); if (onEnd) onEnd(); else resumeWakeSoon(); }
  }

  // Escucha continua en segundo plano (wake word "Jared")
  function startWake() {
    if (unmountedRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || wakeRef.current || commandActiveRef.current) return;
    const rec = new SR();
    rec.lang = speechLang(); rec.continuous = true; rec.interimResults = true;
    rec.onresult = (ev) => {
      if (commandActiveRef.current || thinkingRef.current || listeningRef.current || speakingRef.current) return;
      let txt = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) txt += ev.results[i][0].transcript;
      const ww = cfgRef.current.wake_word || "jared";
      if (txt.toLowerCase().includes(ww)) activateRef.current();
    };
    rec.onend = () => {
      wakeRef.current = null;
      if (!unmountedRef.current && wakeEnabledRef.current && !commandActiveRef.current) {
        setTimeout(() => { try { startWake(); } catch { /* noop */ } }, 500);
      }
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        wakeEnabledRef.current = false; setWake(false);
        try { localStorage.removeItem("jared_wake"); } catch { /* noop */ }
        alert("Necesito permiso del micrófono para el modo manos libres. Actívalo en el navegador.");
      }
    };
    wakeRef.current = rec;
    try { rec.start(); } catch { /* noop */ }
  }

  // Captura de un comando puntual por voz
  function startCommand() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Tu navegador no soporta reconocimiento de voz. Usa Chrome."); return; }
    commandActiveRef.current = true;
    commandSentRef.current = false;
    try { wakeRef.current && wakeRef.current.stop(); } catch { /* noop */ }
    const rec = new SR();
    rec.lang = speechLang(); rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (ev) => { const txt = ev.results[0][0].transcript; commandSentRef.current = true; setInput(txt); sendRef.current(txt); };
    rec.onend = () => {
      setListening(false); commandActiveRef.current = false;
      // Si se capturó un comando, la respuesta hablada reanuda el modo manos libres al terminar.
      if (!commandSentRef.current) resumeWakeSoon();
    };
    rec.onerror = () => {
      setListening(false); commandActiveRef.current = false;
      if (!commandSentRef.current) resumeWakeSoon();
    };
    recRef.current = rec; setListening(true);
    try { rec.start(); } catch { /* noop */ }
  }

  // Activación al detectar la palabra "Jared"
  function activateFromWake() {
    if (unmountedRef.current) return;
    if (commandActiveRef.current || thinkingRef.current || listeningRef.current) return;
    commandActiveRef.current = true;
    greetedRef.current = true; // esta vía ya saluda por voz; evita el saludo doble al abrir
    try { wakeRef.current && wakeRef.current.stop(); } catch { /* noop */ }
    setOpen(true);
    const h = new Date().getHours();
    const ww = cfgRef.current.wake_word || "jared";
    const nombre = ww.charAt(0).toUpperCase() + ww.slice(1);
    const saludo = langCode() === "en"
      ? (h < 12 ? "Good morning" : h < 19 ? "Good afternoon" : "Good evening")
      : (h < 12 ? "Buenos días" : h < 19 ? "Buenas tardes" : "Buenas noches");
    const greet = langCode() === "en"
      ? `${saludo}. ${nombre} here, let's get to work. How can I help you?`
      : `${saludo}. Aquí ${nombre}, comencemos a trabajar. ¿En qué te ayudo?`;
    setJState("normal");
    setMsgs(m => [...m, { role: "jared", text: greet, state: "normal" }]);
    speak(greet, () => { startCommand(); });
  }

  function toggleWake() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Tu navegador no soporta reconocimiento de voz. Usa Chrome (escritorio o Android)."); return; }
    unlockSpeech();
    const nv = !wake;
    setWake(nv);
    wakeEnabledRef.current = nv;
    try { localStorage.setItem("jared_wake", nv ? "1" : "0"); } catch { /* noop */ }
    if (nv) {
      setMsgs(m => [...m, { role: "jared", text: L(
        'Modo manos libres activado. Di "Hola Jared" cuando me necesites.',
        'Hands-free mode enabled. Say "Hello Jared" whenever you need me.'
      ), state: "normal" }]);
      startWake();
    } else {
      try { wakeRef.current && wakeRef.current.stop(); } catch { /* noop */ }
      wakeRef.current = null;
    }
  }

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || thinking) return;
    setMsgs(m => [...m, { role: "user", text: q }, { role: "jared", text: "", state: "warning", streaming: true }]);
    setInput("");
    setThinking(true);
    setStreaming(false);
    setJState("warning");

    // Reemplaza el último mensaje (placeholder de Jared en streaming) con el resultado final.
    const finalize = (patch) => setMsgs(m => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      if (last && last.role === "jared" && last.streaming) copy[copy.length - 1] = { ...last, ...patch, streaming: false };
      return copy;
    });

    // Ejecuta una acción vía el endpoint completo /jared/chat (mantiene toda la lógica de acciones).
    const runAction = async () => {
      const { data } = await axios.post(`${API}/jared/chat`,
        { message: q, session_id: sessionId.current, lang: langCode() }, { withCredentials: true });
      setJState(data.state || "normal");
      finalize({ text: data.reply, state: data.state, downloadUrl: data.download_url, downloadLabel: data.download_label });
      if (data.close) {
        // Jared confirma con su voz y cierra al terminar de hablar (voz o texto)
        try { recRef.current?.stop?.(); } catch {}
        commandActiveRef.current = false;
        const closeNow = () => closeJared();
        if (voiceOn) speak(data.reply, () => setTimeout(closeNow, 500));
        else { speak(data.reply); setTimeout(closeNow, 2500); }
      } else {
        speak(data.reply);
      }
    };

    try {
      // Incluye x-plant-id (planta activa) para que el stream consulte SOLO el
      // tenant abierto; fetch no hereda los headers por defecto de axios.
      const plantId = axios.defaults.headers.common["x-plant-id"];
      const streamHeaders = { "Content-Type": "application/json" };
      if (plantId) streamHeaders["x-plant-id"] = plantId;
      const resp = await fetch(`${API}/jared/chat/stream`, {
        method: "POST", credentials: "include",
        headers: streamHeaders,
        body: JSON.stringify({ message: q, session_id: sessionId.current, lang: langCode() }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream unavailable");

      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "", acc = "", handled = false, started = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let ev; try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (ev.type === "delta") {
            if (!started) { started = true; setStreaming(true); }
            acc += ev.text;
            const cur = acc;
            setMsgs(m => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              if (last && last.role === "jared" && last.streaming) copy[copy.length - 1] = { ...last, text: cur };
              return copy;
            });
          } else if (ev.type === "done") {
            handled = true;
            setJState(ev.state || "normal");
            finalize({ text: ev.reply || acc, state: ev.state,
                       downloadUrl: ev.download_url, downloadLabel: ev.download_label });
            if (ev.close) {
              try { recRef.current?.stop?.(); } catch {}
              commandActiveRef.current = false;
              const closeNow = () => closeJared();
              if (voiceOn) speak(ev.reply || acc, () => setTimeout(closeNow, 500));
              else { speak(ev.reply || acc); setTimeout(closeNow, 2500); }
            } else {
              speak(ev.reply || acc);
            }
          } else if (ev.type === "need_action" || ev.type === "redirect") {
            handled = true;
            await runAction();
          } else if (ev.type === "error") {
            handled = true;
            await runAction();
          }
        }
      }
      if (!handled) await runAction(); // el stream terminó sin evento final: ejecuta la vía completa
    } catch (e) {
      try {
        await runAction(); // fallback: si el streaming falla, usa el endpoint normal
      } catch {
        const err = L("No pude procesar eso. Revisa tu conexión e inténtalo de nuevo.",
                      "I couldn't process that. Please check your connection and try again.");
        setJState("alert");
        finalize({ text: err, state: "alert" });
        speak(err);
      }
    } finally { setThinking(false); setStreaming(false); }
  };

  // Verifica el permiso de micrófono ANTES de conversar; si falta, lo solicita
  // (cuadro nativo) y si queda denegado bloquea la voz y muestra instrucciones.
  async function ensureMic() {
    if (!speechRecognitionAvailable()) {
      alert("Tu navegador no soporta reconocimiento de voz. Usa Chrome.");
      return false;
    }
    let st = await queryPermission("microphone");
    if (st === "granted") return true;
    if (st === "denied") {
      logPermissionEvent("microphone", "blocked_before_conversation");
      setPermAlert("microphone");
      return false;
    }
    const res = await requestMicrophone(); // dispara el cuadro nativo
    if (res === "granted") return true;
    logPermissionEvent("microphone", "blocked_before_conversation", { result: res });
    setPermAlert("microphone");
    return false;
  }

  const toggleListen = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Tu navegador no soporta reconocimiento de voz. Usa Chrome."); return; }
    unlockSpeech();
    if (listening) { recRef.current?.stop(); return; }
    if (!(await ensureMic())) return;
    startCommand();
  };

  const retryMic = async () => {
    const res = await requestMicrophone();
    if (res === "granted") { setPermAlert(null); unlockSpeech(); startCommand(); }
  };

  // Reintenta pedir notificaciones si el usuario las dejó pendientes (una vez por sesión),
  // aprovechando el gesto de abrir Jared.
  const notifAskedRef = useRef(false);
  async function maybeRepromptNotifications() {
    if (notifAskedRef.current) return;
    notifAskedRef.current = true;
    try {
      if (notificationsSupported() && Notification.permission === "default") {
        await requestNotifications();
      }
    } catch { /* noop */ }
  }
  const openJared = () => { unlockSpeech(); setOpen(true); maybeRepromptNotifications(); };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setMsgs(m => [...m, { role: "user", text: "📷 Imagen enviada para clasificar" }]);
      setThinking(true); setJState("warning");
      try {
        const { data } = await axios.post(`${API}/jared/classify-image`,
          { image_base64: reader.result }, { withCredentials: true });
        const tipo = { acto_inseguro: "Acto inseguro", condicion_insegura: "Condición insegura", otro: "Otro" }[data.tipo] || data.tipo;
        const txt = `Hallazgo: ${tipo} · Severidad ${data.severidad}. ${data.descripcion}${data.nom_aplicable ? ` (${data.nom_aplicable})` : ""}`;
        setJState(data.state || "normal");
        const finding = data.tipo === "otro" ? null
          : { tipo: data.tipo, descripcion: data.descripcion, nivel_riesgo: data.severidad, foto_url: reader.result };
        setMsgs(m => [...m, { role: "jared", text: txt, state: data.state, finding }]);
        speak(data.reply || txt);
      } catch {
        setJState("alert");
        setMsgs(m => [...m, { role: "jared", text: "No pude clasificar la imagen.", state: "alert" }]);
      } finally { setThinking(false); e.target.value = ""; }
    };
    reader.readAsDataURL(file);
  };

  const registerFinding = async (finding, idx) => {
    if (thinking) return;
    setThinking(true); setJState("warning");
    try {
      const { data } = await axios.post(`${API}/jared/crear-hallazgo`, finding, { withCredentials: true });
      setJState("done");
      setMsgs(m => m.map((mm, i) => i === idx ? { ...mm, registered: true } : mm)
        .concat([{ role: "jared", text: data.reply, state: "done" }]));
      speak(data.reply);
    } catch {
      setJState("alert");
      setMsgs(m => [...m, { role: "jared", text: "No pude registrar el hallazgo.", state: "alert" }]);
    } finally { setThinking(false); }
  };

  sendRef.current = send;
  activateRef.current = activateFromWake;
  const col = STATE_COLOR[jState] || STATE_COLOR.normal;

  return (
    <>
      <style>{`
        @keyframes jaredPulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.35);opacity:.55}}
        @keyframes hudScan{0%{transform:translateY(-100%)}100%{transform:translateY(2400%)}}
        @keyframes hudDeploy{0%{opacity:0;transform:translateY(28px) scale(.94)}100%{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes hudBar{0%,100%{transform:scaleY(.25)}50%{transform:scaleY(1)}}
        @keyframes hudRise{0%{opacity:0;transform:translateY(10px)}100%{opacity:1;transform:translateY(0)}}
        .jared-hud-widget{transition:border-color .25s, background-color .25s, box-shadow .25s;}
        .jared-hud-widget:hover{border-color:rgba(0,240,255,.55)!important;background:rgba(22,38,66,.85)!important;box-shadow:0 0 18px rgba(0,240,255,.18)}
        .jared-chip{transition:all .2s}
        .jared-chip:hover{background:rgba(0,240,255,.16)!important;box-shadow:0 0 14px rgba(0,240,255,.25);transform:translateY(-1px)}
        @keyframes hudRadar{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
        @keyframes hudBlip{0%,100%{opacity:.2}50%{opacity:1}}
        @keyframes hudCritical{0%,100%{box-shadow:0 0 0 rgba(227,30,36,0)}50%{box-shadow:0 0 18px rgba(227,30,36,.55)}}
        .jared-critical{animation:hudCritical 1.3s ease-in-out infinite!important;border-color:rgba(227,30,36,.55)!important}
        @keyframes hudOrbIn{0%{opacity:0;transform:translate(50px,50px) scale(.3)}100%{opacity:1;transform:translate(0,0) scale(1)}}
        @keyframes hudShrink{0%{opacity:1;transform:translate(0,0) scale(1)}100%{opacity:0;transform:translate(50px,50px) scale(.28)}}
      `}</style>
      {permAlert && (
        <JaredPermissionAlert
          permission={permAlert}
          onRetry={retryMic}
          onClose={() => setPermAlert(null)}
        />
      )}
      {/* Botón flotante (núcleo) */}
      {!open && (
        <button onClick={openJared} data-testid="jared-fab"
          aria-label="Abrir asistente Jared"
          style={{
            position: "fixed", right: 22, bottom: 22, zIndex: 9998,
            width: 68, height: 68, borderRadius: "50%", border: "none", cursor: "pointer",
            background: "radial-gradient(circle at 50% 40%, #0b1220 0%, #060a12 100%)",
            boxShadow: `0 0 22px ${hexA(col.glow, 0.6)}, inset 0 0 12px ${hexA(col.c, 0.4)}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
          <Orb state={jState} active={thinking || listening} speaking={speaking} size={56} />
          {wake && (
            <span data-testid="jared-wake-dot" style={{
              position: "absolute", top: 6, right: 6, width: 12, height: 12, borderRadius: "50%",
              background: "#10E0C4", boxShadow: "0 0 8px #10E0C4",
              border: "2px solid #060a12", animation: "jaredPulse 1.6s ease-in-out infinite",
            }} />
          )}
        </button>
      )}

      {/* Orbe interactivo JARVIS (modo voz por defecto; se expande a panel solo si se pide) */}
      {open && !expanded && (() => {
        const prios = telemetry ? computePriorities(telemetry) : [];
        const prio = prios[0] || null;
        const posStyle = orbCentered
          ? { left: 0, top: 0, right: "auto", bottom: "auto", transform: "none", width: "100vw", height: "100dvh", justifyContent: "center" }
          : { right: "max(16px, env(safe-area-inset-right))", bottom: "max(16px, env(safe-area-inset-bottom))", width: 250 };
        return (
        <>
        {orbCentered && <div data-testid="jared-orb-backdrop" onClick={() => setOrbCentered(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9998, background: "radial-gradient(circle at 50% 45%, rgba(2,6,12,.55), rgba(2,6,12,.85))", backdropFilter: "blur(3px)" }} />}
        <div data-testid="jared-orb"
          style={{
            position: "fixed", zIndex: 9999, ...posStyle,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
            padding: orbCentered ? "10px" : "20px 16px 16px", borderRadius: 20, boxSizing: "border-box",
            background: orbCentered ? "transparent" : "radial-gradient(circle at 50% 30%, rgba(10,20,36,.92), rgba(4,9,17,.92))",
            backdropFilter: orbCentered ? "none" : "blur(16px)", WebkitBackdropFilter: orbCentered ? "none" : "blur(16px)",
            border: orbCentered ? "none" : `1px solid ${hexA(col.c, 0.30)}`,
            boxShadow: orbCentered ? "none" : `0 20px 60px rgba(0,0,0,.7), 0 0 50px ${hexA(col.glow, 0.30)}`,
            animation: closing ? "hudShrink .32s ease both" : "hudOrbIn .42s cubic-bezier(.2,.8,.2,1) both",
            fontFamily: "'JetBrains Mono',ui-monospace,monospace",
          }}>
          {/* Núcleo interactivo grande */}
          {(() => { const orbSz = orbCentered ? Math.round(Math.min(typeof window !== "undefined" ? window.innerWidth : 400, typeof window !== "undefined" ? window.innerHeight : 700) * 0.85) : 150; return (
          <div style={{ position: "relative", width: orbSz, height: orbSz, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            onClick={() => { unlockSpeech(); toggleListen(); }} title="Toca para hablar">
            <Orb state={jState} active={thinking || listening} speaking={speaking} size={orbSz} />
            {speaking && <AudioBars col={col.c} />}
            {listening && <div style={{ position: "absolute", inset: -6, borderRadius: "50%", border: `2px solid ${hexA(col.c, .5)}`, animation: "jaredPulse 1.4s ease-in-out infinite" }} />}
          </div>
          ); })()}
          <div data-testid="jared-orb-status" style={{ color: col.c, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", display: orbCentered ? "none" : "block" }}>
            {speaking ? "◉ Transmitiendo" : thinking ? "◎ Procesando" : listening ? "◉ Escuchando" : "◍ Toca para hablar"}
          </div>
          {/* Top prioridades del día (solo en modo esquina) */}
          {!orbCentered && prios.slice(0, 1).map((p, i) => (
            <div key={i} onClick={() => { unlockSpeech(); send(p.q); }} data-testid={`jared-orb-prio-${i}`}
              style={{ cursor: "pointer", textAlign: "center", color: "#C9DCEF", fontSize: 11.5, lineHeight: 1.4,
                fontFamily: "'IBM Plex Sans',system-ui,sans-serif", maxWidth: "94%" }}>
              <span style={{ color: p.color }}>◆ </span>{p.text}
            </div>
          ))}
          {/* Chips de comando rápido (solo en modo esquina) */}
          {!orbCentered && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
            {[["jared-orb-chip-vencidos", "Vencimientos", "¿Qué tengo vencido o por vencer en toda la planta?"],
              ["jared-orb-chip-capas", "CAPAs", "¿Cuántas CAPAs están abiertas y cuántas vencidas?"],
              ["jared-orb-chip-riesgos", "Riesgos", "¿Qué análisis de riesgo tengo y su estatus?"]].map(c => (
              <button key={c[0]} data-testid={c[0]} className="jared-chip"
                onClick={() => { unlockSpeech(); send(c[2]); }}
                style={{ fontFamily: "inherit", fontSize: 10, letterSpacing: .5, color: col.c, cursor: "pointer",
                  padding: "5px 9px", borderRadius: 18, background: hexA(col.c, .08), border: `1px solid ${hexA(col.c, .35)}` }}>▸ {c[1]}</button>
            ))}
          </div>
          )}
          {/* Controles */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2,
            ...(orbCentered ? { position: "absolute", bottom: "max(24px, env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)" } : {}) }}>
            <button onClick={() => { unlockSpeech(); toggleListen(); }} data-testid="jared-orb-mic" title="Hablar"
              style={{ width: 46, height: 46, borderRadius: "50%", cursor: "pointer", color: col.c,
                border: `2px solid ${hexA(col.c, .55)}`, background: listening ? hexA(col.c, .25) : "rgba(255,255,255,.05)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: listening ? `0 0 20px ${hexA(col.glow, .7)}` : "none" }}>
              <Microphone size={22} weight={listening ? "fill" : "regular"} />
            </button>
            <button onClick={() => setExpanded(true)} data-testid="jared-open-panel" title="Ver panel de datos"
              style={{ ...iconBtn(col), width: 46, height: 46, borderRadius: "50%" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={col.c} strokeWidth="2" strokeLinecap="round">
                <line x1="6" y1="20" x2="6" y2="12" /><line x1="12" y1="20" x2="12" y2="6" /><line x1="18" y1="20" x2="18" y2="14" />
              </svg>
            </button>
            <button onClick={() => setOrbCentered(v => !v)} data-testid="jared-orb-center" title={orbCentered ? "Enviar a la esquina" : "Centrar orbe"}
              style={{ ...iconBtn(col), width: 46, height: 46, borderRadius: "50%" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={col.c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {orbCentered
                  ? <><polyline points="9 3 3 3 3 9" /><polyline points="21 15 21 21 15 21" /><line x1="3" y1="3" x2="10" y2="10" /><line x1="21" y1="21" x2="14" y2="14" /></>
                  : <><polyline points="4 9 4 4 9 4" /><polyline points="20 15 20 20 15 20" /><line x1="4" y1="4" x2="10" y2="10" /><line x1="20" y1="20" x2="14" y2="14" /></>}
              </svg>
            </button>
            <button onClick={closeJared} data-testid="jared-orb-close" title="Cerrar"
              style={{ ...iconBtn(col), width: 46, height: 46, borderRadius: "50%", color: "#8AA0B3", borderColor: "rgba(138,160,179,.3)" }}>
              <X size={20} />
            </button>
          </div>
          <div style={{ color: "#4B6584", fontSize: 9.5, letterSpacing: .5, display: orbCentered ? "none" : "block" }}>
            {wake ? "Manos libres activo · di «Jared»" : "Toca el orbe o el micrófono"}
          </div>
        </div>
        </>
        );
      })()}

      {/* Consola HUD holográfica (modo expandido: solo cuando se pide algo visual/texto) */}
      {open && expanded && (
        <div data-testid="jared-panel"
          style={{
            position: "fixed",
            right: "max(12px, env(safe-area-inset-right))",
            bottom: "max(12px, env(safe-area-inset-bottom))",
            left: "auto", zIndex: 9999,
            width: "min(420px, calc(100vw - 24px))",
            height: "min(680px, calc(100dvh - 24px))",
            borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", boxSizing: "border-box",
            background: "rgba(5,11,20,0.90)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
            border: "1px solid rgba(0,240,255,0.30)",
            boxShadow: `0 24px 70px rgba(0,0,0,.7), 0 0 46px ${hexA(col.glow, 0.22)}, inset 0 0 60px rgba(0,240,255,0.04)`,
            fontFamily: "'JetBrains Mono','Share Tech Mono',ui-monospace,monospace",
            animation: closing ? "hudShrink .32s ease both" : "hudDeploy .42s cubic-bezier(.2,.8,.2,1) both",
          }}
          className="jared-panel-responsive">
          {/* Retículas de esquina */}
          {[["top",8,"left",8],["top",8,"right",8],["bottom",8,"left",8],["bottom",8,"right",8]].map((p,i)=>(
            <div key={i} style={{ position:"absolute", [p[0]]:p[1], [p[2]]:p[3], width:16, height:16, zIndex:5, pointerEvents:"none",
              borderTop: p[0]==="top"?`2px solid ${hexA(col.c,.7)}`:"none", borderBottom: p[0]==="bottom"?`2px solid ${hexA(col.c,.7)}`:"none",
              borderLeft: p[2]==="left"?`2px solid ${hexA(col.c,.7)}`:"none", borderRight: p[2]==="right"?`2px solid ${hexA(col.c,.7)}`:"none" }} />
          ))}
          {/* Barrido láser durante análisis */}
          {thinking && <div style={{ position:"absolute", left:0, right:0, top:0, height:2, zIndex:4, pointerEvents:"none",
            background:`linear-gradient(90deg, transparent, ${col.c}, transparent)`, boxShadow:`0 0 12px ${col.c}`, animation:"hudScan 2.4s linear infinite" }} />}

          {/* Header telemetría */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", position:"relative", zIndex:3,
            borderBottom: `1px solid ${hexA(col.c, 0.18)}`, background: "linear-gradient(90deg, rgba(0,240,255,0.07), transparent)" }}>
            <div style={{ position:"relative", display:"flex", alignItems:"center" }}>
              <Orb state={jState} active={thinking || listening} speaking={speaking} size={44} />
              {speaking && <AudioBars col={col.c} />}
            </div>
            <div style={{ flex: 1, minWidth:0 }}>
              <div style={{ color: "#E2F1FF", fontWeight: 800, fontSize: 14, letterSpacing: 2 }}>J.A.R.E.D</div>
              <div data-testid="jared-status-indicator" style={{ color: col.c, fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform:"uppercase", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {speaking ? "◉ TRANSMITIENDO" : thinking ? "◎ PROCESANDO" : listening ? "◉ ESCUCHANDO" : `◍ ${(col.label || "EN LÍNEA").toUpperCase()}`}
              </div>
            </div>
            <button onClick={toggleWake} title={wake ? "Manos libres ON" : "Manos libres"} data-testid="jared-wake-toggle"
              style={{ background: "transparent", border: "none", cursor: "pointer", color: wake ? col.c : "#4B6584", padding: 3 }}><Ear size={18} weight={wake ? "fill" : "regular"} /></button>
            <button onClick={() => setVoiceOn(v => !v)} title={voiceOn ? "Voz ON" : "Voz OFF"} data-testid="jared-voice-toggle"
              style={{ background: "transparent", border: "none", cursor: "pointer", color: voiceOn ? col.c : "#4B6584", padding: 3 }}><Waveform size={18} weight={voiceOn ? "fill" : "regular"} /></button>
            <button onClick={() => setShowInput(v => !v)} title="Teclado" data-testid="jared-keyboard-toggle"
              style={{ background: "transparent", border: "none", cursor: "pointer", color: showInput ? col.c : "#4B6584", padding: 3 }}><Keyboard size={18} weight={showInput ? "fill" : "regular"} /></button>
            <button onClick={() => setExpanded(false)} title="Volver al orbe" data-testid="jared-minimize"
              style={{ background: "transparent", border: "none", cursor: "pointer", color: "#7D9BBA", padding: 3, fontSize: 16, lineHeight: 1 }}>—</button>
            <button onClick={closeJared} data-testid="jared-close"
              style={{ background: "transparent", border: "none", cursor: "pointer", color: "#7D9BBA", padding: 3 }}><X size={18} /></button>
          </div>

          {/* Cuerpo HUD */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 12, position:"relative", zIndex:2 }}>
            {/* SISTEMA HSE - grid de telemetría */}
            {telemetry && (() => {
              const criticalCount = Object.values(telemetry).filter(m => m?.status === "critical").length;
              const prio = computePriority(telemetry);
              const askModule = (q) => { unlockSpeech(); send(q); };
              return (
              <div>
                <div style={{ color: col.c, fontSize: 10, letterSpacing: 3, marginBottom: 8, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ width:5, height:5, borderRadius:"50%", background:col.c, boxShadow:`0 0 8px ${col.c}` }} />SISTEMA HSE · TELEMETRÍA
                  <span style={{ flex:1, height:1, background:`linear-gradient(90deg, ${hexA(col.c,.4)}, transparent)` }} />
                  <MiniRadar critical={criticalCount} />
                </div>

                {/* Sugerencia proactiva / prioridad del día */}
                {prio && (
                  <div data-testid="jared-priority-banner" onClick={() => askModule(prio.q)}
                    style={{ cursor:"pointer", marginBottom:8, padding:"9px 11px", borderRadius:8, display:"flex", alignItems:"center", gap:10,
                      background: hexA(prio.color, 0.10), border:`1px solid ${hexA(prio.color,.45)}`, boxShadow:`inset 0 0 20px ${hexA(prio.color,.08)}` }}>
                    <span style={{ width:8, height:8, borderRadius:"50%", background:prio.color, boxShadow:`0 0 10px ${prio.color}`, animation:"hudBlip 1.2s infinite", flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ color:prio.color, fontSize:9, letterSpacing:2 }}>◆ PRIORIDAD DEL DÍA</div>
                      <div style={{ color:"#E2F1FF", fontSize:12.5, fontFamily:"'IBM Plex Sans',system-ui,sans-serif", lineHeight:1.35 }}>{prio.text}</div>
                    </div>
                    <span style={{ color:prio.color, fontSize:10, letterSpacing:1, whiteSpace:"nowrap" }}>ATENDER ▸</span>
                  </div>
                )}

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <HudWidget tid="jared-widget-seguridad" label="SEGURIDAD" status={telemetry.seguridad?.status} delay={0}
                    active={insight.mod === "seguridad"} onClick={() => openInsight("seguridad")}
                    metrics={[["Actos/Cond.", telemetry.seguridad?.actos ?? 0]]} />
                  <HudWidget tid="jared-widget-salud" label="SALUD OCUP." status={telemetry.salud?.status} delay={0.08}
                    active={insight.mod === "salud"} onClick={() => openInsight("salud")}
                    metrics={[["Consultas", telemetry.salud?.consultas ?? 0],["Incapac.", telemetry.salud?.incapacidades ?? 0]]} />
                  <HudWidget tid="jared-widget-ambiente" label="AMBIENTE" status={telemetry.ambiente?.status} delay={0.16}
                    active={insight.mod === "ambiente"} onClick={() => openInsight("ambiente")}
                    metrics={[["Vencidos", telemetry.ambiente?.vencidos ?? 0],["Por vencer", telemetry.ambiente?.por_vencer ?? 0]]} />
                  <HudWidget tid="jared-widget-capacitacion" label="CAPACITACIÓN" status={telemetry.capacitacion?.status} delay={0.24}
                    active={insight.mod === "capacitacion"} onClick={() => openInsight("capacitacion")}
                    metrics={[["Registros", telemetry.capacitacion?.registros ?? 0]]} />
                  <HudWidget tid="jared-widget-capa" label="CAPA · CORRECTIVAS" status={telemetry.capa?.status} delay={0.32}
                    active={insight.mod === "capa"} onClick={() => openInsight("capa")}
                    metrics={[["Abiertas", telemetry.capa?.abiertas ?? 0],["Vencidas", telemetry.capa?.vencidas ?? 0]]} />
                  <HudWidget tid="jared-widget-permisos-trabajo" label="PERMISOS TRABAJO" status={telemetry.permisos_trabajo?.status} delay={0.4}
                    active={insight.mod === "permisos_trabajo"} onClick={() => openInsight("permisos_trabajo")}
                    metrics={[["Activos", telemetry.permisos_trabajo?.activos ?? 0]]} />
                </div>

                {insight.mod && (
                  <InsightPanel state={insight} col={col}
                    onClose={() => setInsight({ mod: null, loading: false, data: null })}
                    onAsk={(q) => askModule(q)} />
                )}
              </div>
              );
            })()}

            {/* IA ADAPTATIVA · Perfil de tu planta (resumen) */}
            {telemetry && profile && (
              <ProfilePanel col={col} profile={profile} />
            )}

            {/* IA AUTÓNOMA · Autopiloto de Jared */}
            {telemetry && (
              <AutopilotPanel col={col} state={autopilot} onExec={execAutopilot}
                onToggle={toggleAutopilot} onOpenLog={loadAutopilotLog}
                onCloseLog={() => setAutopilot(a => ({ ...a, showLog: false }))} />
            )}


            {/* Comandos rápidos */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {[["jared-action-btn-analizar-riesgos","Analizar riesgos","¿Qué análisis de riesgo tengo y su estatus?"],
                ["jared-action-btn-vencidos","Vencimientos","¿Qué tengo vencido o por vencer en toda la planta?"],
                ["jared-action-btn-capas","CAPAs abiertas","¿Cuántas CAPAs están abiertas y cuántas vencidas?"]].map(a=>(
                <button key={a[0]} data-testid={a[0]} className="jared-chip"
                  onClick={()=>{ unlockSpeech(); send(a[2]); }}
                  style={{ fontFamily:"inherit", fontSize:10.5, letterSpacing:.5, color:col.c, cursor:"pointer",
                    padding:"6px 10px", borderRadius:20, background:hexA(col.c,.08), border:`1px solid ${hexA(col.c,.35)}` }}>▸ {a[1]}</button>
              ))}
            </div>

            {/* Readout de misión (sin burbujas de chat) */}
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {msgs.map((m, i) => (
                <div key={i} style={{ animation:"hudRise .3s ease both" }}>
                  {m.role === "user" ? (
                    <div style={{ textAlign:"right", color:"#7D9BBA", fontSize:11, letterSpacing:.5 }}>
                      <span style={{ color:col.c }}>▸ </span>{m.text}
                    </div>
                  ) : (
                    <div style={{ borderLeft:`2px solid ${hexA((STATE_COLOR[m.state]||STATE_COLOR.normal).c,.7)}`,
                      background: hexA((STATE_COLOR[m.state]||STATE_COLOR.normal).c, 0.06), padding:"8px 11px", borderRadius:"0 8px 8px 0" }}>
                      <div style={{ color:(STATE_COLOR[m.state]||STATE_COLOR.normal).c, fontSize:9, letterSpacing:2, marginBottom:3 }}>JARED //</div>
                      <div style={{ color:"#D0E2F5", fontSize:13, lineHeight:1.5, whiteSpace:"pre-wrap", fontFamily:"'IBM Plex Sans',system-ui,sans-serif" }}>{m.text}</div>
                      {m.downloadUrl && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${m.downloadUrl}`} target="_blank" rel="noopener noreferrer" data-testid="jared-download-pdf"
                          style={{ display:"inline-block", marginTop:6, padding:"6px 12px", fontSize:11, fontWeight:700, textDecoration:"none",
                            borderRadius:6, border:`1px solid ${hexA(col.c,.5)}`, background:hexA(col.c,.9), color:"#04121a" }}>⬇ DESCARGAR PDF{m.downloadLabel ? ` · ${m.downloadLabel}` : ""}</a>
                      )}
                      {m.finding && !m.registered && (
                        <button onClick={() => registerFinding(m.finding, i)} data-testid="jared-register-finding"
                          style={{ marginTop:6, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", borderRadius:6,
                            border:`1px solid ${hexA(col.c,.5)}`, background:hexA(col.c,.9), color:"#04121a" }}>✓ REGISTRAR HALLAZGO</button>
                      )}
                      {m.registered && <div style={{ marginTop:4, fontSize:10, color:STATE_COLOR.done.c, letterSpacing:1 }}>✓ HALLAZGO REGISTRADO</div>}
                    </div>
                  )}
                </div>
              ))}
              {thinking && !streaming && (
                <div style={{ display:"flex", gap:8, alignItems:"center", color:col.c, fontSize:11, letterSpacing:1 }}>
                  <span style={{ display:"inline-flex", gap:3 }}>
                    {[0,1,2,3].map(b=><span key={b} style={{ width:3, height:12, background:col.c, borderRadius:2, transformOrigin:"bottom", animation:`hudBar .9s ease-in-out ${b*0.12}s infinite` }} />)}
                  </span> PROCESANDO…
                </div>
              )}
            </div>
          </div>

          {/* Entrada: modo voz por defecto; la barra de texto se muestra solo si se solicita */}
          {showInput ? (
            <div style={{ padding: 12, borderTop: `1px solid ${hexA(col.c, 0.18)}`, display: "flex", gap: 8, alignItems: "center", boxSizing: "border-box", width: "100%" }}>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onImage} style={{ display: "none" }} data-testid="jared-image-input" />
              <button onClick={() => fileRef.current?.click()} title="Enviar imagen (hallazgo)" data-testid="jared-image-btn"
                style={iconBtn(col)}><ImageSquare size={18} /></button>
              <button onClick={toggleListen} title="Hablar" data-testid="jared-mic-btn"
                style={{ ...iconBtn(col), background: listening ? hexA(col.c, 0.25) : "rgba(255,255,255,0.05)" }}>
                <Microphone size={18} weight={listening ? "fill" : "regular"} />
              </button>
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send()}
                disabled={thinking}
                placeholder="Escribe o habla a Jared…" data-testid="jared-input" autoFocus
                style={{ flex: 1, minWidth: 0, background: "rgba(255,255,255,0.06)", border: `1px solid ${hexA(col.c, 0.2)}`,
                  borderRadius: 10, padding: "9px 12px", color: "#eafcff", fontSize: 13.5, outline: "none", boxSizing: "border-box",
                  opacity: thinking ? 0.6 : 1 }} />
              <button onClick={() => { unlockSpeech(); send(); }} disabled={thinking} title="Enviar" data-testid="jared-send-btn"
                style={{ ...iconBtn(col), background: hexA(col.c, 0.9), color: "#04121a" }}>
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </div>
          ) : (
            <div style={{ padding: "16px 12px", borderTop: `1px solid ${hexA(col.c, 0.18)}`, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, boxSizing: "border-box", width: "100%" }}>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onImage} style={{ display: "none" }} data-testid="jared-image-input" />
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <button onClick={() => fileRef.current?.click()} title="Enviar imagen (hallazgo)" data-testid="jared-image-btn"
                  style={iconBtn(col)}><ImageSquare size={18} /></button>
                <button onClick={() => { unlockSpeech(); toggleListen(); }} title="Toca para hablar" data-testid="jared-mic-btn"
                  style={{
                    width: 60, height: 60, borderRadius: "50%", border: `2px solid ${hexA(col.c, 0.6)}`, cursor: "pointer",
                    background: listening ? hexA(col.c, 0.28) : "rgba(255,255,255,0.05)", color: col.c,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: listening ? `0 0 22px ${hexA(col.glow, 0.7)}` : "none",
                    animation: listening ? "jaredPulse 1.4s ease-in-out infinite" : "none",
                  }}>
                  <Microphone size={26} weight={listening ? "fill" : "regular"} />
                </button>
                <button onClick={() => setShowInput(true)} title="Escribir" data-testid="jared-keyboard-open"
                  style={iconBtn(col)}><Keyboard size={18} /></button>
              </div>
              <div style={{ color: "#7c8ea0", fontSize: 11 }}>
                {listening ? "Escuchando… habla ahora" : (speaking ? "Jared está hablando…" : "Toca el micrófono para hablar")}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function iconBtn(col) {
  return {
    width: 38, height: 38, minWidth: 38, flexShrink: 0, borderRadius: 10, cursor: "pointer",
    border: `1px solid ${hexA(col.c, 0.25)}`, background: "rgba(255,255,255,0.05)",
    color: col.c, display: "flex", alignItems: "center", justifyContent: "center",
  };
}

// Ecualizador de audio alrededor del núcleo cuando Jared habla
function AudioBars({ col }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "space-between", pointerEvents: "none" }}>
      {[0, 1].map(side => (
        <div key={side} style={{ display: "flex", gap: 2, alignItems: "center", flexDirection: side ? "row-reverse" : "row" }}>
          {[0, 1, 2].map(b => (
            <span key={b} style={{ width: 2.5, height: 16, background: col, borderRadius: 2, transformOrigin: "center",
              boxShadow: `0 0 6px ${col}`, animation: `hudBar ${0.5 + b * 0.12}s ease-in-out ${b * 0.09}s infinite` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

const HUD_STATUS = {
  optimal: { c: "#00FF9D", t: "ÓPTIMO" },
  attention: { c: "#FFB800", t: "ATENCIÓN" },
  critical: { c: "#E31E24", t: "CRÍTICO" },
};

// Widget de misión (tarjeta HUD) para un módulo del Sistema HSE
function HudWidget({ tid, label, status, metrics, delay = 0, onClick, active = false }) {
  const s = HUD_STATUS[status] || HUD_STATUS.optimal;
  const isCrit = status === "critical";
  return (
    <div data-testid={tid} onClick={onClick} className={"jared-hud-widget" + (isCrit ? " jared-critical" : "")}
      style={{ background: active ? "rgba(0,240,255,0.10)" : "rgba(15,26,46,0.65)",
        border: active ? "1px solid rgba(0,240,255,0.55)" : "1px solid rgba(0,240,255,0.14)", borderRadius: 8,
        padding: "9px 10px", position: "relative", overflow: "hidden", cursor: onClick ? "pointer" : "default",
        animation: `hudRise .4s ease ${delay}s both` }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", background: s.c, boxShadow: `0 0 10px ${s.c}` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ color: "#00F0FF", fontSize: 9, letterSpacing: 1.5 }}>{label}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 4, color: s.c, fontSize: 8, letterSpacing: 1 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.c, boxShadow: `0 0 6px ${s.c}` }} />{s.t}
        </span>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        {metrics.map(([k, v], i) => (
          <div key={i}>
            <div style={{ color: "#E2F1FF", fontSize: 22, fontWeight: 800, lineHeight: 1, letterSpacing: -0.5 }}>{v}</div>
            <div style={{ color: "#4B6584", fontSize: 8.5, letterSpacing: 0.5, marginTop: 2 }}>{k}</div>
          </div>
        ))}
      </div>
      {onClick && (
        <span style={{ position:"absolute", bottom:5, right:8, color: active ? "#00F0FF" : "#4B6584",
          fontSize:8.5, letterSpacing:1, display:"flex", alignItems:"center", gap:3 }}>
          ¿POR QUÉ? <span style={{ fontSize:11, transform: active ? "rotate(90deg)" : "none", transition:"transform .2s" }}>▸</span>
        </span>
      )}
    </div>
  );
}

const SEV_COLOR = { alta: "#FF3B3B", media: "#F5C518", baja: "#10E0C4" };

// Panel "¿Por qué?" (IA Explicativa): variables + registros que originan la alerta + narrativa IA
function InsightPanel({ state, col, onClose, onAsk }) {
  const d = state.data;
  return (
    <div data-testid="jared-insight-panel" style={{ marginTop: 8, borderRadius: 10, overflow: "hidden",
      background: "rgba(8,16,30,0.92)", border: `1px solid ${hexA(col.c, .4)}`, animation: "hudRise .3s ease both" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 11px",
        borderBottom: `1px solid ${hexA(col.c, .22)}`, background: hexA(col.c, .07) }}>
        <span style={{ color: col.c, fontSize: 10, letterSpacing: 2, flex: 1 }}>
          ◆ ¿POR QUÉ? · {d?.titulo || state.mod}
        </span>
        <button data-testid="jared-insight-close" onClick={onClose}
          style={{ background: "none", border: "none", color: "#7C93B0", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>✕</button>
      </div>
      <div style={{ padding: "10px 11px", maxHeight: 260, overflowY: "auto" }}>
        {state.loading && (
          <div data-testid="jared-insight-loading" style={{ color: "#7C93B0", fontSize: 12, padding: "8px 0" }}>
            Analizando variables con IA…
          </div>
        )}
        {!state.loading && d?.error && (
          <div style={{ color: "#FF8080", fontSize: 12 }}>No pude cargar el análisis. Intenta de nuevo.</div>
        )}
        {!state.loading && d && !d.error && (
          <>
            {/* Variables que disparan la alerta */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {(d.variables || []).map((v, i) => (
                <div key={i} style={{ background: hexA(col.c, .08), border: `1px solid ${hexA(col.c, .28)}`,
                  borderRadius: 7, padding: "5px 9px" }}>
                  <div style={{ color: "#E2F1FF", fontSize: 15, fontWeight: 800, lineHeight: 1 }}>{String(v.valor)}</div>
                  <div style={{ color: "#7C93B0", fontSize: 8.5, letterSpacing: .3, marginTop: 2 }}>{v.nombre}</div>
                </div>
              ))}
            </div>

            {/* Tendencia adaptativa: vs. el histórico de TU planta */}
            {d.tendencia?.texto && (
              <div data-testid="jared-insight-tendencia" style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8,
                background: hexA(d.tendencia.tendencia_pct > 15 ? "#FF3B3B" : d.tendencia.tendencia_pct < -15 ? "#10E0C4" : "#F5C518", .08),
                border: `1px solid ${hexA(d.tendencia.tendencia_pct > 15 ? "#FF3B3B" : d.tendencia.tendencia_pct < -15 ? "#10E0C4" : "#F5C518", .3)}` }}>
                <div style={{ color: "#9FB4CC", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 3, display: "flex", alignItems: "center", gap: 5 }}>
                  <span>{d.tendencia.tendencia_pct > 15 ? "▲" : d.tendencia.tendencia_pct < -15 ? "▼" : "≈"}</span>
                  TENDENCIA · APRENDIDA DE TU PLANTA
                </div>
                <div style={{ color: "#CFE3F5", fontSize: 11.5, lineHeight: 1.4 }}>{d.tendencia.texto}</div>
              </div>
            )}

            {/* Áreas recurrentes (histórico) */}
            {(d.top_areas || []).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ color: "#4B6584", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 4 }}>ÁREAS RECURRENTES (HISTÓRICO)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {d.top_areas.map((a, i) => (
                    <span key={i} style={{ fontSize: 10.5, color: "#CFE3F5", background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, padding: "3px 8px" }}>
                      {a.area} · {a.pct}%
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Narrativa IA: por qué importa */}
            {d.narrativa && (
              <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: "rgba(16,224,196,0.06)",
                border: "1px solid rgba(16,224,196,0.22)" }}>
                <div style={{ color: "#10E0C4", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 3 }}>◆ ANÁLISIS DE JARED</div>
                <div style={{ color: "#CFE3F5", fontSize: 12, lineHeight: 1.45,
                  fontFamily: "'IBM Plex Sans',system-ui,sans-serif" }}>{d.narrativa}</div>
              </div>
            )}

            {/* Recomendaciones priorizadas */}
            {(d.recomendaciones || []).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ color: col.c, fontSize: 8.5, letterSpacing: 1.5, marginBottom: 4 }}>▸ QUÉ HACER</div>
                {d.recomendaciones.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 7, marginBottom: 4 }}>
                    <span style={{ color: col.c, fontSize: 11, flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ color: "#CFE3F5", fontSize: 11.5, lineHeight: 1.4 }}>{r}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Registros que originan la alerta (drivers) */}
            {(d.drivers || []).length > 0 && (
              <div>
                <div style={{ color: "#4B6584", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 5 }}>
                  REGISTROS QUE LA ORIGINAN ({d.drivers.length})
                </div>
                {d.drivers.map((x, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0",
                    borderTop: i ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                      background: SEV_COLOR[x.severidad] || SEV_COLOR.baja,
                      boxShadow: `0 0 6px ${SEV_COLOR[x.severidad] || SEV_COLOR.baja}` }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#E2F1FF", fontSize: 12, fontWeight: 600 }}>{x.label}</div>
                      <div style={{ color: "#8CA3BE", fontSize: 10.5, lineHeight: 1.35 }}>{x.detalle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(d.drivers || []).length === 0 && !d.narrativa && (
              <div style={{ color: "#10E0C4", fontSize: 12 }}>Sin incidencias en este módulo. 🟢</div>
            )}

            <button data-testid="jared-insight-ask" onClick={() => onAsk(`Dame más detalle y contexto sobre ${d.titulo}`)}
              style={{ marginTop: 10, width: "100%", padding: "7px", borderRadius: 7, cursor: "pointer",
                background: hexA(col.c, .1), border: `1px solid ${hexA(col.c, .35)}`, color: col.c,
                fontSize: 10.5, letterSpacing: .5, fontFamily: "inherit" }}>
              ▸ Preguntar a Jared por más detalle
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// IA Adaptativa: tarjeta resumen del "perfil de tu planta" (promedios + tendencia + áreas)
function ProfilePanel({ col, profile }) {
  const TEAL = "#10E0C4";
  const seg = profile.seguridad || {}, capa = profile.capa || {}, inc = profile.incapacidades || {}, perm = profile.permisos || {};
  const trendChip = (t) => {
    const pct = t?.tendencia_pct ?? 0;
    const up = pct > 15, down = pct < -15;
    const c = up ? "#FF3B3B" : down ? TEAL : "#F5C518";
    return <span style={{ color: c, fontSize: 9, whiteSpace: "nowrap" }}>{up ? "▲" : down ? "▼" : "≈"} {Math.abs(pct)}%</span>;
  };
  const row = (label, t) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
      <span style={{ flex: 1, color: "#CFE3F5", fontSize: 11 }}>{label}</span>
      <span style={{ color: "#8CA3BE", fontSize: 9.5, whiteSpace: "nowrap" }}>prom {t?.prom_mensual ?? 0}/mes · hoy {t?.mes_actual ?? 0}</span>
      {trendChip(t)}
    </div>
  );
  return (
    <div data-testid="jared-profile-panel" style={{ marginTop: 8, marginBottom: 4, borderRadius: 10, overflow: "hidden",
      background: "rgba(8,16,30,0.75)", border: `1px solid ${hexA(TEAL, .25)}` }}>
      <div style={{ padding: "8px 11px", borderBottom: `1px solid ${hexA(TEAL, .15)}`, background: hexA(TEAL, .05),
        color: TEAL, fontSize: 10, letterSpacing: 2, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: TEAL, boxShadow: `0 0 8px ${TEAL}` }} />
        PERFIL DE TU PLANTA · {profile.meses_analizados || 6} MESES
      </div>
      <div style={{ padding: "6px 11px 9px" }}>
        {row("Hallazgos (seguridad)", seg)}
        {row("CAPAs creadas", capa)}
        {row("Incapacidades", inc)}
        {(perm.serie || perm.prom_mensual != null) && row("Vencimientos permisos", perm)}
        {capa.cierre_dias_prom != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <span style={{ flex: 1, color: "#CFE3F5", fontSize: 11 }}>Cierre promedio de CAPAs</span>
            <span style={{ color: TEAL, fontSize: 12, fontWeight: 700 }}>~{capa.cierre_dias_prom} días</span>
          </div>
        )}
        {(seg.serie || []).length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ color: "#4B6584", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 6 }}>
              TENDENCIA HALLAZGOS · 6 MESES
            </div>
            <MiniTrend serie={seg.serie} color={TEAL} />
          </div>
        )}
        {(inc.serie || []).some(s => s.valor > 0) && (
          <div style={{ marginTop: 12 }}>
            <div style={{ color: "#4B6584", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 6 }}>
              TENDENCIA INCAPACIDADES · 6 MESES
            </div>
            <MiniTrend serie={inc.serie} color="#F5C518" />
          </div>
        )}
        {(perm.serie || []).some(s => s.valor > 0) && (
          <div style={{ marginTop: 12 }}>
            <div style={{ color: "#4B6584", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 6 }}>
              VENCIMIENTOS DE PERMISOS · 6 MESES
            </div>
            <MiniTrend serie={perm.serie} color="#FF7A45" />
          </div>
        )}
        {(seg.top_areas || []).length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ color: "#4B6584", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 4 }}>ÁREAS RECURRENTES</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {seg.top_areas.map((a, i) => (
                <span key={i} style={{ fontSize: 10.5, color: "#CFE3F5", background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, padding: "3px 8px" }}>{a.area} · {a.pct}%</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Mini-gráfica de barras (tendencia de 6 meses) — CSS puro
function MiniTrend({ serie, color }) {
  const max = Math.max(1, ...serie.map(s => s.valor || 0));
  return (
    <div data-testid="jared-mini-trend" style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 48 }}>
      {serie.map((s, i) => {
        const h = Math.round(((s.valor || 0) / max) * 38) + 2;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <span style={{ color: "#9FB4CC", fontSize: 8.5 }}>{s.valor}</span>
            <div title={`${s.mes}: ${s.valor}`} style={{ width: "100%", maxWidth: 20, height: h, borderRadius: 3,
              background: `linear-gradient(180deg, ${color}, ${hexA(color, .35)})`,
              boxShadow: (s.valor || 0) > 0 ? `0 0 6px ${hexA(color, .5)}` : "none" }} />
            <span style={{ color: "#5F7695", fontSize: 8 }}>{s.mes}</span>
          </div>
        );
      })}
    </div>
  );
}

// IA Autónoma: panel del Autopiloto (propuestas + confirmación 1 clic + config + bitácora)
function AutopilotPanel({ col, state, onExec, onToggle, onOpenLog, onCloseLog }) {
  const AMBER = "#F5C518", TEAL = "#10E0C4";
  const sevCol = { alta: "#FF3B3B", media: AMBER };
  const hasSug = (state.suggestions || []).length > 0;
  return (
    <div data-testid="jared-autopilot-panel" style={{ marginTop: 4, borderRadius: 10, overflow: "hidden",
      background: "rgba(8,16,30,0.75)", border: `1px solid ${hexA(TEAL, .3)}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 11px",
        borderBottom: `1px solid ${hexA(TEAL, .18)}`, background: hexA(TEAL, .06) }}>
        <span style={{ color: TEAL, fontSize: 10, letterSpacing: 2, flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: state.enabled ? TEAL : "#4B6584",
            boxShadow: state.enabled ? `0 0 8px ${TEAL}` : "none" }} />
          AUTOPILOTO · JARED
        </span>
        <button data-testid="jared-autopilot-log-btn" onClick={onOpenLog}
          style={{ background: "none", border: "none", color: "#7C93B0", cursor: "pointer", fontSize: 9, letterSpacing: 1 }}>
          BITÁCORA
        </button>
        {/* Interruptor on/off */}
        <button data-testid="jared-autopilot-toggle" onClick={() => onToggle(!state.enabled)}
          title={state.enabled ? "Autopiloto activo" : "Autopiloto en pausa"}
          style={{ width: 34, height: 18, borderRadius: 10, border: "none", cursor: "pointer", position: "relative",
            background: state.enabled ? hexA(TEAL, .5) : "rgba(255,255,255,0.12)", transition: "background .2s" }}>
          <span style={{ position: "absolute", top: 2, left: state.enabled ? 18 : 2, width: 14, height: 14,
            borderRadius: "50%", background: state.enabled ? TEAL : "#7C93B0", transition: "left .2s" }} />
        </button>
      </div>

      <div style={{ padding: "9px 11px", maxHeight: 220, overflowY: "auto" }}>
        {!state.enabled && (
          <div style={{ color: "#7C93B0", fontSize: 11.5 }}>Autopiloto en pausa. Actívalo para que Jared proponga acciones.</div>
        )}

        {state.enabled && state.showLog && (
          <div data-testid="jared-autopilot-log">
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
              <span style={{ color: "#9FB4CC", fontSize: 8.5, letterSpacing: 1.5, flex: 1 }}>BITÁCORA DE ACCIONES</span>
              <button onClick={onCloseLog} style={{ background: "none", border: "none", color: "#7C93B0", cursor: "pointer", fontSize: 9 }}>VOLVER ▸</button>
            </div>
            {(state.log || []).length === 0 && <div style={{ color: "#7C93B0", fontSize: 11 }}>Sin acciones registradas aún.</div>}
            {(state.log || []).map((x, i) => (
              <div key={i} style={{ padding: "5px 0", borderTop: i ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                <div style={{ color: "#CFE3F5", fontSize: 11 }}>{x.resultado}</div>
                <div style={{ color: "#5F7695", fontSize: 9 }}>{(x.at || "").slice(0, 16).replace("T", " ")} · {x.by}</div>
              </div>
            ))}
          </div>
        )}

        {state.enabled && !state.showLog && (
          <>
            {!hasSug && (
              <div data-testid="jared-autopilot-empty" style={{ color: TEAL, fontSize: 11.5 }}>
                Sin propuestas por ahora. Jared vigila tus datos y te avisará. 🟢
              </div>
            )}
            {hasSug && (
              <div style={{ color: "#9FB4CC", fontSize: 8.5, letterSpacing: 1.5, marginBottom: 6 }}>
                JARED PROPONE ({state.suggestions.length}) · CONFIRMA CON 1 CLIC
              </div>
            )}
            {state.suggestions.map((s) => (
              <div key={s.id} data-testid={`jared-autopilot-sug-${s.id}`}
                style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "8px 0",
                  borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                  background: sevCol[s.severidad] || AMBER, boxShadow: `0 0 6px ${sevCol[s.severidad] || AMBER}` }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#E2F1FF", fontSize: 12, fontWeight: 600 }}>{s.titulo}</div>
                  <div style={{ color: "#8CA3BE", fontSize: 10.5, lineHeight: 1.35 }}>{s.descripcion}</div>
                </div>
                {state.canExecute && (
                  <button data-testid={`jared-autopilot-exec-${s.id}`} disabled={state.busy === s.id}
                    onClick={() => onExec(s)}
                    style={{ flexShrink: 0, alignSelf: "center", padding: "5px 10px", borderRadius: 7, cursor: "pointer",
                      background: hexA(TEAL, .12), border: `1px solid ${hexA(TEAL, .45)}`, color: TEAL,
                      fontSize: 10, letterSpacing: .5, fontFamily: "inherit", whiteSpace: "nowrap",
                      opacity: state.busy === s.id ? .5 : 1 }}>
                    {state.busy === s.id ? "…" : (s.tipo && s.tipo.startsWith("recordatorio") ? "✓ Notificar" : (s.tipo && s.tipo.startsWith("resumen") ? "✓ Enviar" : "✓ Programar"))}
                  </button>
                )}
              </div>
            ))}
            {hasSug && !state.canExecute && (
              <div style={{ color: "#7C93B0", fontSize: 9.5, marginTop: 6 }}>
                Solo Gerente, Supervisor o Coordinador EHS pueden confirmar estas acciones.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function MiniRadar({ critical = 0 }) {
  const c = critical > 0 ? "#E31E24" : "#00F0FF";
  return (
    <div title={critical > 0 ? `${critical} módulo(s) en estado crítico` : "Sistemas nominales"}
      data-testid="jared-radar" style={{ position: "relative", width: 26, height: 26, flexShrink: 0 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `1px solid ${hexA(c, .45)}` }} />
      <div style={{ position: "absolute", inset: 6, borderRadius: "50%", border: `1px solid ${hexA(c, .3)}` }} />
      <div style={{ position: "absolute", left: "50%", top: "50%", width: 1, height: "50%", background: hexA(c, .25), transform: "translateX(-50%)" }} />
      <div style={{ position: "absolute", left: 0, top: 0, width: "50%", height: "50%", transformOrigin: "bottom right",
        animation: "hudRadar 2.6s linear infinite",
        background: `conic-gradient(from 0deg, ${hexA(c, .55)}, transparent 60%)`, borderTopLeftRadius: "100%" }} />
      {critical > 0 && (
        <span style={{ position: "absolute", left: "62%", top: "30%", width: 4, height: 4, borderRadius: "50%",
          background: c, boxShadow: `0 0 8px ${c}`, animation: "hudBlip 1s infinite" }} />
      )}
    </div>
  );
}

// Sugerencias proactivas: acciones urgentes del día calculadas desde la telemetría (ordenadas por severidad)
function computePriorities(tel) {
  if (!tel) return [];
  const RED = "#E31E24", AMBER = "#FFB800";
  const plu = (n, s, p) => (n === 1 ? s : p);
  const items = [];
  if (tel.ambiente?.vencidos) { const n = tel.ambiente.vencidos; items.push({ sev: 3, color: RED, text: `${n} ${plu(n, "permiso ambiental vencido", "permisos ambientales vencidos")} — ${plu(n, "regularízalo", "regularízalos")}`, q: "Dame el detalle de permisos ambientales vencidos: número, autoridad y vencimiento" }); }
  if (tel.capa?.vencidas) { const n = tel.capa.vencidas; items.push({ sev: 3, color: RED, text: `${n} ${plu(n, "acción correctiva CAPA vencida", "acciones correctivas CAPA vencidas")}`, q: "Dame el detalle de las CAPAs vencidas: folio, responsable y fecha límite" }); }
  if (tel.seguridad?.actos) { const n = tel.seguridad.actos; items.push({ sev: tel.seguridad.status === "critical" ? 3 : 2, color: tel.seguridad.status === "critical" ? RED : AMBER, text: `${n} ${plu(n, "acto o condición insegura por atender", "actos y condiciones inseguras por atender")}`, q: "Dame el detalle de actos y condiciones inseguras abiertas: folios, área y responsable" }); }
  const incap = tel.salud?.incapacidades || 0;
  if (incap) items.push({ sev: tel.salud.status === "critical" ? 3 : 2, color: tel.salud.status === "critical" ? RED : AMBER, text: `${incap} ${plu(incap, "incapacidad activa en seguimiento", "incapacidades activas en seguimiento")}`, q: "Dame el detalle de incapacidades activas: nombre, tipo y fechas" });
  if (tel.ambiente?.por_vencer) { const n = tel.ambiente.por_vencer; items.push({ sev: 2, color: AMBER, text: `${n} ${plu(n, "permiso ambiental por vencer", "permisos ambientales por vencer")}`, q: "Dame el detalle de permisos ambientales por vencer" }); }
  const cons = tel.salud?.consultas || 0;
  if (cons) items.push({ sev: 1, color: AMBER, text: `${cons} ${plu(cons, "consulta médica en proceso", "consultas médicas en proceso")}`, q: "Dame el detalle de consultas médicas en proceso" });
  items.sort((a, b) => b.sev - a.sev);
  return items;
}
function computePriority(tel) {
  return computePriorities(tel)[0] || null;
}
